(() => {
  'use strict';
  const init = root => {
    if (root.dataset.sampleReady) return;
    root.dataset.sampleReady = 'true';
    const config = JSON.parse(root.querySelector('[data-sample-config]')?.textContent || '{}');
    const labels = config.labels || {};
    const text = (key, values = {}) => Object.entries(values).reduce((value, [name, replacement]) => value.replaceAll('__' + name.toUpperCase() + '__', String(replacement)).replaceAll('{{ ' + name + ' }}', String(replacement)).replaceAll('{{' + name + '}}', String(replacement)), labels[key] || key);
    const cards = [...root.querySelectorAll('[data-sample-card]')];
    const optionsByCard = new Map(cards.map(card => [card, [...card.querySelectorAll('[data-sample-variant]')]]));
    const byId = new Map();
    for (const [card, options] of optionsByCard) {
      for (const option of options) {
        if (option.value) byId.set(option.value, {card, option, ...option.dataset, id: option.value});
      }
    }
    const sampleIds = new Set(byId.keys());
    const filters = root.querySelector('[data-sample-filters]');
    const lines = root.querySelector('[data-sample-lines]');
    const emptyCart = root.querySelector('[data-sample-cart-empty]');
    const subtotal = root.querySelector('[data-sample-subtotal]');
    const otherItems = root.querySelector('[data-sample-other-items]');
    const checkout = root.querySelector('[data-sample-checkout]');
    const status = root.querySelector('[data-sample-status]');
    const error = root.querySelector('[data-sample-error]');
    const retry = root.querySelector('[data-sample-retry]');
    const tray = root.querySelector('[data-sample-tray]');
    if (!lines || !checkout) return;
    const normalize = value => String(value || '').normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
    const baseDisabled = new WeakMap();
    let busy = false;
    let loaded = false;
    let currentCart = null;
    let sampleCount = 0;
    let requestNumber = 0;
    const route = (url, fallback) => {
      const path = new URL(url || fallback, window.location.origin);
      if (path.origin !== window.location.origin) throw new Error('Invalid cart route');
      path.pathname = path.pathname.replace(/\.js$/, '') + '.js';
      return path.pathname + path.search;
    };
    const cartURL = route(root.dataset.cartUrl, '/cart');
    const addURL = route(root.dataset.addUrl, '/cart/add');
    const changeURL = route(root.dataset.changeUrl, '/cart/change');
    const currency = () => currentCart?.currency || root.dataset.currency || 'USD';
    const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: currency()}).format(Number(amount || 0) / 100);
    const isSample = item => sampleIds.has(String(item.variant_id)) || item.properties?._cw_sample === 'true';
    const announce = message => { if (status) status.textContent = message; };
    const showError = message => {
      if (error) { error.textContent = message; error.hidden = !message; }
    };
    const setBusy = value => {
      busy = value;
      root.dataset.sampleBusy = String(value);
      if (tray) tray.setAttribute('aria-busy', String(value));
      root.querySelectorAll('[data-sample-add] button, [data-sample-add] input:not([type="hidden"]), [data-sample-add] select, [data-line-action], [data-line-quantity]').forEach(control => {
        if (!baseDisabled.has(control)) baseDisabled.set(control, control.disabled);
        control.disabled = value || !loaded || baseDisabled.get(control);
      });
      checkout.disabled = value || !loaded || sampleCount === 0 || root.dataset.checkoutEnabled !== 'true';
      if (retry) retry.disabled = value;
    };
    const getJSON = async (url, options = {}) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal, headers: {Accept: 'application/json', ...(options.body ? {'Content-Type': 'application/json'} : {}), ...options.headers}});
        const body = await response.json();
        if (!response.ok) {
          const problem = new Error(body.description || body.message || text('update_error'));
          problem.confirmedRejection = true;
          throw problem;
        }
        return body;
      } finally { window.clearTimeout(timer); }
    };
    const element = (tag, className, content) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (content !== undefined) node.textContent = content;
      return node;
    };
    const button = (label, key, action, title, disabled = false) => {
      const node = element('button', 'cw-sample-line-button', label);
      node.type = 'button';
      node.dataset.lineKey = key;
      node.dataset.lineAction = action;
      node.setAttribute('aria-label', text(action, {title}));
      node.disabled = disabled;
      return node;
    };
    const captureFocus = () => {
      const active = document.activeElement;
      if (!lines.contains(active)) return null;
      return {key: active.dataset.lineKey, action: active.dataset.lineAction, quantity: active.hasAttribute('data-line-quantity')};
    };
    const restoreFocus = focus => {
      if (!focus) return;
      const target = [...lines.querySelectorAll('[data-line-key]')].find(node => node.dataset.lineKey === focus.key && (focus.quantity ? node.hasAttribute('data-line-quantity') : node.dataset.lineAction === focus.action));
      const fallback = lines.querySelector('[data-line-quantity]') || emptyCart;
      if (target && !target.disabled) target.focus({preventScroll: true});
      else if (fallback) { if (fallback === emptyCart) fallback.tabIndex = -1; fallback.focus({preventScroll: true}); }
    };
    const render = cart => {
      currentCart = cart;
      const samples = cart.items.filter(isSample);
      sampleCount = samples.reduce((sum, item) => sum + item.quantity, 0);
      const fragment = document.createDocumentFragment();
      for (const item of samples) {
        const record = byId.get(String(item.variant_id));
        const title = record?.title || item.title || item.product_title;
        const article = element('li', 'cw-sample-line');
        article.dataset.sampleLine = item.key;
        const imageURL = record?.image || item.image || item.featured_image?.url;
        if (imageURL) {
          const image = element('img', 'cw-sample-line-image');
          image.src = imageURL;
          image.alt = text('image_alt', {title});
          image.width = 60;
          image.height = 76;
          image.loading = 'lazy';
          if (record?.previewStyle) {
            const stage = element('span', 'cw-sample-line-image-stage');
            const frame = element('span', 'cw-sample-line-image-frame');
            frame.setAttribute('style', record.previewStyle);
            image.className = 'cw-sample-line-image-source';
            frame.append(image);
            stage.append(frame);
            article.append(stage);
          } else article.append(image);
        }
        const copy = element('div', 'cw-sample-line-copy');
        copy.append(element('h3', '', title));
        const dimensions = item.properties?.['Sample size'] || record?.dimensions;
        if (dimensions) copy.append(element('p', 'cw-sample-line-unit-price', dimensions));
        copy.append(element('p', 'cw-sample-line-unit-price', text('each', {price: money(item.final_price)})));
        const controls = element('div', 'cw-sample-line-controls');
        controls.append(button('−', item.key, 'decrease', title, item.quantity <= 1));
        const quantity = element('input', 'cw-sample-line-quantity');
        quantity.type = 'number';
        quantity.inputMode = 'numeric';
        quantity.min = '1';
        quantity.step = '1';
        quantity.value = String(item.quantity);
        quantity.dataset.lineKey = item.key;
        quantity.dataset.lineQuantity = '';
        quantity.setAttribute('aria-label', text('quantity', {title}));
        controls.append(quantity, button('+', item.key, 'increase', title));
        const remove = button(text('remove', {title}), item.key, 'remove', title);
        remove.classList.add('cw-sample-line-remove');
        copy.append(controls, remove);
        article.append(copy, element('strong', 'cw-sample-line-total', money(item.final_line_price)));
        fragment.append(article);
      }
      lines.replaceChildren(fragment);
      if (emptyCart) emptyCart.hidden = samples.length !== 0;
      if (subtotal) subtotal.textContent = money(samples.reduce((sum, item) => sum + item.final_line_price, 0));
      const otherCount = cart.items.filter(item => !isSample(item)).reduce((sum, item) => sum + item.quantity, 0);
      if (otherItems) { otherItems.hidden = otherCount === 0; otherItems.textContent = text(otherCount === 1 ? 'other_items_one' : 'other_items', {count: otherCount}); }
      root.querySelectorAll('[data-sample-cart-count]').forEach(node => { node.textContent = String(sampleCount); });
      const cartLink = document.getElementById('cart-icon-bubble');
      if (cartLink) {
        cartLink.setAttribute('aria-label', text('cart_count', {count: cart.item_count}));
        let badge = cartLink.querySelector('.cart-count-bubble');
        if (!cart.item_count) badge?.remove();
        else {
          if (!badge) {
            badge = element('div', 'cart-count-bubble');
            cartLink.append(badge);
          }
          const visibleCount = element('span', '', cart.item_count < 100 ? String(cart.item_count) : '99+');
          visibleCount.setAttribute('aria-hidden', 'true');
          badge.replaceChildren(visibleCount, element('span', 'visually-hidden', text('cart_count', {count: cart.item_count})));
        }
      }
      root.dispatchEvent(new CustomEvent('cw:samples-cart-updated', {bubbles: true, detail: {cart}}));
    };
    const refresh = async () => {
      if (busy) return;
      const request = ++requestNumber;
      setBusy(true);
      showError('');
      announce(text('loading'));
      try {
        const cart = await getJSON(cartURL);
        if (request !== requestNumber) return;
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        loaded = true;
        render(cart);
        if (retry) retry.hidden = true;
        announce(sampleCount ? text('updated') : text('cart_empty'));
      } catch (problem) {
        loaded = false;
        showError(text('load_error'));
        announce('');
        if (retry) retry.hidden = false;
      } finally { setBusy(false); }
    };
    const mutate = async (url, payload, successMessage, focus) => {
      if (busy || !loaded) return;
      setBusy(true);
      showError('');
      try {
        await getJSON(url, {method: 'POST', body: JSON.stringify(payload)});
        const cart = await getJSON(cartURL);
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        render(cart);
        loaded = true;
        announce(successMessage);
        if (retry) retry.hidden = true;
      } catch (problem) {
        loaded = false;
        showError(problem.confirmedRejection ? problem.message : text('update_error'));
        announce('');
        if (retry) retry.hidden = false;
      } finally {
        setBusy(false);
        restoreFocus(focus);
      }
    };
    const selectVariant = (card, option) => {
      if (!option) return;
      const record = option.dataset;
      const technical = card.querySelector('[data-sample-technical-dialog]');
      if (technical) {
        const finishLabel = technical.querySelector('[data-technical-finish]');
        if (finishLabel) finishLabel.textContent = record.finish || '';
        const code = record.code || '';
        const collection = code === 'PGW' ? 'palermo' : code.startsWith('LS') ? 'luxe' : code.startsWith('ZS') ? 'zenit' : code.startsWith('SS') ? 'syncron' : 'torino';
        technical.querySelectorAll('[data-technical-collection]').forEach(node => { node.hidden = node.dataset.technicalCollection !== collection; });
        const material = code === 'SEO' ? 'oak' : ['SH', 'SC', 'OM', 'OT', 'HR', 'HE'].includes(code) ? 'birch' : code === 'ER' ? 'edgeline_ridge' : 'painted';
        technical.querySelectorAll('[data-technical-material]').forEach(node => { node.hidden = node.dataset.technicalMaterial !== material; });
      }
      card.dataset.variantId = option.value;
      for (const name of ['finish', 'surface', 'title', 'price', 'image']) card.dataset[name] = record[name] || '';
      const image = card.querySelector('[data-sample-image]');
      const frame = card.querySelector('[data-sample-image-frame]');
      const preview = card.querySelector('[data-cw-sample-open]');
      const fallback = card.querySelector('[data-sample-image-error]');
      if (frame) {
        frame.classList.toggle('cw-web-sample-frame', !!record.previewStyle);
        frame.classList.toggle('cw-sample-plain-frame', !record.previewStyle);
        record.previewStyle ? frame.setAttribute('style', record.previewStyle) : frame.removeAttribute('style');
      }
      if (image) {
        image.alt = record.title || '';
        image.removeAttribute('srcset');
        if (record.image) image.src = record.image;
        else image.removeAttribute('src');
      }
      if (preview) {
        preview.hidden = !record.image;
        preview.dataset.sampleUrl = record.zoom || record.image || '';
        preview.dataset.sampleTitle = record.title || '';
        preview.dataset.sampleStyle = record.previewStyle || '';
        preview.setAttribute('aria-label', text('enlarge', {title: record.title}));
      }
      if (fallback) fallback.hidden = !!record.image;
      const price = card.querySelector('[data-sample-price]');
      if (price) price.textContent = Number(record.price) > 0 ? money(record.price) : text('price_pending');
      const available = record.available === 'true' && Number(record.price) > 0 && !!option.value;
      const form = card.querySelector('[data-sample-add]');
      const submit = form?.querySelector('[type="submit"]');
      const quantity = form?.elements.namedItem('quantity');
      if (submit) { submit.textContent = text(available ? 'add' : 'unavailable'); baseDisabled.set(submit, !available); submit.disabled = !available || busy || !loaded; }
      if (quantity) { baseDisabled.set(quantity, !available); quantity.disabled = !available || busy || !loaded; quantity.setAttribute('aria-label', text('quantity', {title: record.title})); }
      const link = card.querySelector('[data-sample-finish-link]');
      if (link) {
        const url = new URL(link.href);
        url.searchParams.set('door', card.dataset.door);
        url.searchParams.set('finish', record.finish || '');
        link.href = url.pathname + url.search;
      }
    };
    cards.forEach(card => {
      const technical = card.querySelector('[data-sample-technical-dialog]');
      const technicalOpen = card.querySelector('[data-sample-technical-open]');
      if (technical && technicalOpen) {
        technicalOpen.addEventListener('click', () => { if (!technical.open) technical.showModal(); });
        technical.querySelector('[data-sample-technical-close]')?.addEventListener('click', () => technical.close());
        technical.addEventListener('click', event => {
          if (event.target !== technical) return;
          const rect = technical.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) technical.close();
        });
        technical.addEventListener('close', () => technicalOpen.focus({preventScroll: true}));
      }
      const select = card.querySelector('[data-sample-finish]');
      const image = card.querySelector('[data-sample-image]');
      const preview = card.querySelector('[data-cw-sample-open]');
      const fallback = card.querySelector('[data-sample-image-error]');
      image?.addEventListener('error', () => { if (fallback) fallback.hidden = false; if (preview) preview.hidden = true; });
      image?.addEventListener('load', () => { if (fallback) fallback.hidden = true; if (preview) preview.hidden = false; });
      select?.addEventListener('change', () => selectVariant(card, select.selectedOptions[0]));
      if (select) selectVariant(card, select.selectedOptions[0] || select.options[0]);
    });
    root.querySelectorAll('[data-sample-add]').forEach(form => {
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (busy || !loaded) return;
        const quantity = Number(form.elements.namedItem('quantity')?.value || 1);
        const id = String(form.elements.namedItem('id')?.value || '');
        const record = byId.get(id);
        if (!Number.isSafeInteger(quantity) || quantity < 1) { showError(text('quantity_error')); form.elements.namedItem('quantity')?.focus(); return; }
        if (!sampleIds.has(id) || !record || record.card !== form.closest('[data-sample-card]') || record.available !== 'true' || !(Number(record.price) > 0) || form.querySelector('[type="submit"]')?.disabled) { showError(text('unavailable')); return; }
        const properties = {_cw_sample: 'true'};
        for (const [key, value] of new FormData(form).entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        properties._cw_sample = 'true';
        mutate(addURL, {items: [{id, quantity, properties}]}, text('added', {title: record.title}), null);
      });
    });
    lines.addEventListener('click', event => {
      const control = event.target.closest('[data-line-action]');
      if (!control || busy || !loaded) return;
      const item = currentCart.items.find(row => row.key === control.dataset.lineKey);
      if (!item || !isSample(item)) return;
      const action = control.dataset.lineAction;
      const quantity = action === 'remove' ? 0 : Math.max(1, item.quantity + (action === 'increase' ? 1 : -1));
      mutate(changeURL, {id: item.key, quantity}, text(action === 'remove' ? 'removed' : 'updated'), captureFocus());
    });
    lines.addEventListener('change', event => {
      const control = event.target.closest('[data-line-quantity]');
      if (!control || busy || !loaded) return;
      const item = currentCart.items.find(row => row.key === control.dataset.lineKey);
      if (!item || !isSample(item)) return;
      const quantity = Number(control.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { control.value = String(item.quantity); showError(text('quantity_error')); return; }
      if (quantity !== item.quantity) mutate(changeURL, {id: item.key, quantity}, text('updated'), captureFocus());
    });
    checkout.closest('form')?.addEventListener('submit', event => {
      if (busy || !loaded || sampleCount === 0 || root.dataset.checkoutEnabled !== 'true') event.preventDefault();
    });
    retry?.addEventListener('click', refresh);
    document.addEventListener('click', event => {
      if (!root.isConnected || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !event.target.closest('#cart-icon-bubble')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) { announce(text('loading')); return; }
      window.location.assign(root.dataset.cartUrl || '/cart');
    }, true);
    if (filters) {
      const fields = Object.fromEntries(['q', 'style', 'surface'].map(name => [name, filters.elements.namedItem(name)]));
      ['style', 'surface'].forEach(name => {
        const select = fields[name];
        if (!select) return;
        const existing = new Set([...select.options].map(option => option.value));
        const values = name === 'style' ? cards.map(card => card.dataset.style) : [...optionsByCard.values()].flat().map(option => option.dataset.surface);
        for (const value of [...new Set(values.filter(Boolean))]) {
          if (!existing.has(value)) select.add(new Option(value, value));
        }
      });
      const applyFilters = writeURL => {
        const values = Object.fromEntries(Object.entries(fields).map(([name, field]) => [name, field?.value || '']));
        let count = 0;
        cards.forEach(card => {
          const select = card.querySelector('[data-sample-finish]');
          const selected = select?.selectedOptions[0];
          const matching = optionsByCard.get(card).filter(option => {
            const record = option.dataset;
            return (!values.surface || record.surface === values.surface) && (!values.q || normalize([record.title, record.style, record.surface, record.finish, record.code].join(' ')).includes(normalize(values.q.trim())));
          });
          const matchingOptions = new Set(matching);
          for (const option of optionsByCard.get(card)) {
            const included = matchingOptions.has(option);
            option.hidden = !included;
            option.disabled = !included || option.dataset.available !== 'true';
          }
          select?.querySelectorAll('optgroup').forEach(group => {
            group.hidden = ![...group.children].some(option => !option.hidden);
          });
          const matches = (!values.style || card.dataset.style === values.style) && matching.length > 0;
          card.hidden = !matches;
          if (matches) {
            count += 1;
            const available = matching.find(option => option.dataset.available === 'true');
            if (select && (!matchingOptions.has(selected) || (selected.dataset.available !== 'true' && available))) {
              const next = available || matching[0];
              select.value = next.value;
              selectVariant(card, next);
            }
          }
        });
        const countNode = root.querySelector('[data-sample-count]');
        if (countNode) countNode.textContent = text(count === 1 ? 'count_one' : 'count', {count});
        const empty = root.querySelector('[data-sample-empty]');
        if (empty) empty.hidden = count !== 0;
        if (writeURL) {
          const url = new URL(window.location.href);
          for (const [name, value] of Object.entries(values)) value ? url.searchParams.set(name, value) : url.searchParams.delete(name);
          url.searchParams.delete('door');
          url.searchParams.delete('finish');
          window.history.replaceState(window.history.state, '', url);
        }
      };
      const restoreFilters = () => {
        const params = new URL(window.location.href).searchParams;
        Object.entries(fields).forEach(([name, field]) => {
          if (!field) return;
          let value = params.get(name) || '';
          if (name === 'q' && !value) value = params.get('finish') || '';
          if (name === 'style' && !value && params.get('door')) value = cards.find(card => card.dataset.door === params.get('door'))?.dataset.style || '';
          field.value = field.tagName === 'SELECT' && ![...field.options].some(option => option.value === value) ? '' : value;
        });
        applyFilters(false);
      };
      filters.addEventListener('submit', event => { event.preventDefault(); applyFilters(true); });
      filters.addEventListener('input', () => applyFilters(true));
      filters.addEventListener('change', () => applyFilters(true));
      filters.addEventListener('reset', () => window.setTimeout(() => applyFilters(true), 0));
      window.addEventListener('popstate', restoreFilters);
      restoreFilters();
    }
    window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
    refresh();
  };
  const start = () => document.querySelectorAll('[data-cw-sample-shop]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('castlewood:content-ready', start);
})();


